// The usage monitor's client (module 10 U70). Its own fetch helpers, as the super-admin surface
// keeps its own, so this page is worked on apart from the rest of the portal. The types mirror
// apps/api/src/usage/view.ts; nothing is decided here that the API did not decide.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';

export type UsageWindow = 'today' | '7d' | '30d' | 'custom';

export interface TokenCounts { input: number; cacheWrite: number; cacheRead: number; output: number }
export interface PromptShare { prompt: string; calls: number; usd: number }
export interface ModelShare { model: string; label: string; calls: number; usd: number; priced: boolean }

export interface LaneView {
  purpose: string; name: string; does: string;
  calls: number; failed: number; usd: number; sharePct: number; perCallUsd: number;
  tokens: TokenCounts; unpricedCalls: number; wastedUsd: number; priced: boolean;
  prompts: PromptShare[]; models: ModelShare[];
}
export interface SpendRow {
  id: string; name: string; what: string; usd: number; sharePct: number;
  how: 'measured' | 'charged'; lane: LaneView | null; noMeter: string | null;
}
export interface ModelView { model: string; label: string; kind: 'api' | 'cobrain' | 'unpriced'; calls: number; usd: number; tokens: TokenCounts }
export interface DayView { day: string; usd: number; calls: number; lanes: Array<{ purpose: string; usd: number }> }
export interface HourView { hour: string; usd: number; calls: number }
export interface CeilingView {
  purpose: string; name: string; ceilingUsd: number; spentTodayUsd: number; headroomUsd: number;
  reached: boolean; scope: 'store' | 'person'; note: string;
}

export type SourceState = 'measured' | 'billed' | 'not_connected' | 'needs_credential' | 'not_metered';
export interface CostSource {
  id: string; name: string; what: string; state: SourceState;
  usd: number | null; netUsd: number | null; basis: string; todo: string | null;
}
export interface AccountCost {
  usd: number; from: string; to: string; readAt: string;
  lines: Array<{ description: string; usd: number }>;
}
export type ReceiptKind = 'api_credit' | 'plan_usage' | 'other';
export interface ReceiptTotals {
  paidUsd: number; netUsd: number; count: number; latestAt: string | null; unread: number;
  byKind: Array<{ kind: ReceiptKind; name: string; paidUsd: number; netUsd: number; count: number }>;
}

export interface CostView {
  usd: number; netUsd: number; taxUsd: number;
  sources: CostSource[]; missing: number;
  creditBoughtUsd: number; creditBoughtNetUsd: number;
  account: AccountCost | null; accountError: string | null;
  paid: ReceiptTotals | null;
}

export interface UsageView {
  window: UsageWindow;
  from: string; to: string; asAt: string; rolledAt: string | null; ready: boolean;
  dayStart: string; dayEnd: string;
  totals: { usd: number; calls: number; failed: number; tokens: TokenCounts; unpricedCalls: number; unpricedModels: string[]; wastedUsd: number };
  today: { usd: number; calls: number; failed: number; wastedUsd: number; lastFullHourUsd: number; thisHourUsd: number; forecastUsd: number; hoursLeft: number };
  lanes: LaneView[];
  spend: SpendRow[];
  models: ModelView[];
  days: DayView[];
  hours: HourView[];
  cache: { readTokens: number; writeTokens: number; savedUsd: number };
  ceilings: CeilingView[];
  notCounted: string[];
  cost: CostView;
  store: { tenantId: string; name: string };
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export const usage = {
  /** A custom window carries two calendar days; every other window ignores them. */
  read: (window: UsageWindow, from?: string, to?: string) => {
    const q = new URLSearchParams({ window });
    if (window === 'custom' && from !== undefined && to !== undefined) { q.set('from', from); q.set('to', to); }
    return fetch(`/api/usage?${q.toString()}`, { credentials: 'same-origin' }).then((r) => json<UsageView>(r));
  },
};
